import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { FileText, Upload as UploadIcon, X, Check, Loader2, FileJson } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Button from '../../System Design/Button';
import bookService from '../../services/bookService';
import { useLanguage } from '../../contexts/LanguageContext';

const Upload = () => {
  const [pdfFile, setPdfFile] = useState(null);
  const [jsonFile, setJsonFile] = useState(null);
  const [title, setTitle] = useState('');
  const [generateQcm, setGenerateQcm] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [error, setError] = useState('');
  const [createdBook, setCreatedBook] = useState(null);
  const [isQueued, setIsQueued] = useState(false);

  const { t } = useLanguage();

  const onPdfDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      setPdfFile(acceptedFiles[0]);
    }
  }, []);

  const onJsonDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      setJsonFile(acceptedFiles[0]);
    }
  }, []);

  const { 
    getRootProps: getPdfRootProps, 
    getInputProps: getPdfInputProps, 
    isDragActive: isPdfDragActive 
  } = useDropzone({
    onDrop: onPdfDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: false,
    maxSize: 50 * 1024 * 1024, // 50MB
  });

  const { 
    getRootProps: getJsonRootProps, 
    getInputProps: getJsonInputProps, 
    isDragActive: isJsonDragActive 
  } = useDropzone({
    onDrop: onJsonDrop,
    accept: {
      'application/json': ['.json']
    },
    multiple: false,
    maxSize: 5 * 1024 * 1024, // 5MB
  });

  const handleRemovePdfFile = (e) => {
    e.stopPropagation();
    setPdfFile(null);
    setUploadComplete(false);
  };

  const handleRemoveJsonFile = (e) => {
    e.stopPropagation();
    setJsonFile(null);
    setUploadComplete(false);
  };

  const navigate = useNavigate();

  const handleUpload = async () => {
    const isJsonOnly = !pdfFile && !!jsonFile;

    // Si aucun PDF et aucun JSON, on reste sur l'erreur PDF manquant
    if (!pdfFile && !jsonFile) {
      setError(t('upload.errors.noPdf'));
      return;
    }

    // Titre obligatoire uniquement pour le flux PDF
    if (!isJsonOnly && !title.trim()) {
      setError(t('upload.errors.noTitle'));
      return;
    }
    
    setIsUploading(true);
    setError('');
    
    try {
      let newBook;

      if (isJsonOnly) {
        // Importer un livre directement depuis un JSON exporté (sans PDF)
        const formData = new FormData();
        if (title.trim()) {
          formData.append('title', title.trim());
        }
        formData.append('json_structure_file', jsonFile);
        formData.append('generate_qcm', generateQcm ? 'true' : 'false');

        console.log("Import d'un livre depuis JSON uniquement:", {
          title: title.trim() || '(repris du JSON)',
          json_file: jsonFile.name,
        });

        newBook = await bookService.importBookFromJson(formData);
      } else {
        // Flux existant: upload PDF (avec éventuellement un JSON de structure)
        const formData = new FormData();
        formData.append('title', title.trim());
        formData.append('pdf_file', pdfFile);
        formData.append('generate_qcm', generateQcm ? 'true' : 'false');

        if (jsonFile) {
          formData.append('json_structure_file', jsonFile);
        }

        console.log('Envoi des données du livre:', {
          title: title.trim(),
          pdf_file: pdfFile.name,
          json_file: jsonFile ? jsonFile.name : 'Aucun fichier JSON'
        });

        newBook = await bookService.createBook(formData);
      }
      setCreatedBook(newBook);
      const queued = (newBook?.processing_status === 'queued' || newBook?.processing_status === 'processing');
      setIsQueued(queued);
      setUploadComplete(true);
      
      // Si le traitement est déjà terminé (cas rare), on peut rediriger. Sinon, on reste sur place.
      if (!queued) {
        setTimeout(() => {
          navigate(`/documents/${newBook.id}`);
        }, 1500);
      }
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
      const errorMessage = error.response?.data?.message || 
                         error.response?.data?.detail || 
                         t('upload.errors.generic');
      setError(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="flex-1 p-4 sm:p-6 md:p-8 overflow-y-auto">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-xl sm:text-2xl font-bold mb-6 sm:mb-8">{t('upload.pageTitle')}</h1>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 sm:p-6 border-b">
            <h2 className="text-base sm:text-lg font-semibold mb-4">{t('upload.sectionTitle')}</h2>
            
            <div className="mb-6 sm:mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('upload.fields.titleLabel')}
              </label>
              <input
                type="text"
                className="w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder={t('upload.fields.titlePlaceholder')}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={isUploading || uploadComplete}
              />
              
              {/* Catégories */}
              {/* <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Catégorie
                </label>
                <div className="flex gap-2 flex-wrap">
                  <button className="px-3 py-1.5 text-sm font-medium bg-primary-100 text-primary rounded-md hover:bg-primary-200">
                    Professionnel
                  </button>
                  <button className="px-3 py-1.5 text-sm font-medium bg-accent-100 text-accent rounded-md hover:bg-accent-200">
                    Éducation
                  </button>
                  <button className="px-3 py-1.5 text-sm font-medium bg-success-100 text-success rounded-md hover:bg-success-200">
                    Personnel
                  </button>
                  <button className="px-3 py-1.5 text-sm font-medium bg-warning-100 text-warning rounded-md hover:bg-warning-200">
                    Loisirs
                  </button>
                </div>
              </div> */}
            </div>
            
            <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
              {t('upload.help.main')}
            </p>
            <p className="text-xs text-gray-500 mb-4 sm:mb-6">
              {t('upload.help.sub')}
            </p>
          </div>
          
          {/* Zone d'upload PDF */}
          <div className="mb-4 sm:mb-6 px-4 sm:px-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('upload.pdf.label')} <span className="text-red-500">*</span>
            </label>
            <div 
              {...getPdfRootProps()} 
              className={`p-4 sm:p-6 text-center border-2 border-dashed rounded-lg transition-colors ${
                isPdfDragActive ? 'border-primary bg-primary-50' : 'border-gray-200 hover:border-primary'
              }`}
            >
              <input {...getPdfInputProps()} />
              
              {pdfFile ? (
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center text-primary mb-3">
                    <FileText size={24} />
                  </div>
                  <p className="font-medium text-gray-900">{pdfFile.name}</p>
                  <p className="text-sm text-gray-500 mb-4">
                    {(pdfFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  <button 
                    onClick={handleRemovePdfFile}
                    className="text-sm text-red-600 hover:text-red-800 flex items-center gap-1"
                  >
                    <X size={16} /> {t('documents.card.tooltipDelete')}
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center text-gray-400 mb-3">
                    <FileText size={24} />
                  </div>
                  <p className="text-sm text-gray-600">
                    {isPdfDragActive 
                      ? t('upload.pdf.dropActive') 
                      : t('upload.pdf.dropDefault')}
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    {t('upload.pdf.formats')}
                  </p>
                </div>
              )}
            </div>
          </div>
          
          {/* Zone d'upload JSON */}
          <div className="mb-4 sm:mb-6 px-4 sm:px-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('upload.json.label')} <span className="text-gray-400">{t('upload.json.optional')}</span>
            </label>
            <div 
              {...getJsonRootProps()} 
              className={`p-4 sm:p-6 text-center border-2 border-dashed rounded-lg transition-colors ${
                isJsonDragActive ? 'border-accent bg-accent-50' : 'border-gray-200 hover:border-accent'
              }`}
            >
              <input {...getJsonInputProps()} />
              
              {jsonFile ? (
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 bg-accent-100 rounded-full flex items-center justify-center text-accent mb-3">
                    <FileJson size={24} />
                  </div>
                  <p className="font-medium text-gray-900">{jsonFile.name}</p>
                  <p className="text-sm text-gray-500 mb-4">
                    {(jsonFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  <button 
                    onClick={handleRemoveJsonFile}
                    className="text-sm text-red-600 hover:text-red-800 flex items-center gap-1"
                  >
                    <X size={16} /> {t('documents.card.tooltipDelete')}
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center text-gray-400 mb-3">
                    <FileJson size={24} />
                  </div>
                  <p className="text-sm text-gray-600">
                    {isJsonDragActive 
                      ? t('upload.json.dropActive') 
                      : t('upload.json.dropDefault')}
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    {t('upload.json.formats')}
                  </p>
                </div>
              )}
            </div>
          </div>
          
          {/* Option de génération des QCM */}
          <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 border-t border-b">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={generateQcm}
                onChange={(e) => setGenerateQcm(e.target.checked)}
                disabled={isUploading || uploadComplete}
                className="mt-1 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary focus:ring-2"
              />
              <div className="flex-1">
                <span className="text-sm font-medium text-gray-900">
                  {t('upload.qcm.label')}
                </span>
                <p className="text-xs text-gray-500 mt-1">
                  {t('upload.qcm.help')}
                </p>
              </div>
            </label>
          </div>
          
          <div className="p-4 sm:p-6">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-xs sm:text-sm">
                {error}
              </div>
            )}
            
            <div className="flex flex-col sm:flex-row justify-end gap-3">
              <Button
                onClick={() => {
                  setPdfFile(null);
                  setJsonFile(null);
                  setTitle('');
                  setGenerateQcm(true);
                  setError('');
                }}
                variant="secondary"
                size="md"
                disabled={isUploading || uploadComplete}
                className="w-full sm:w-auto"
              >
                {t('upload.buttons.cancel')}
              </Button>
              
              <Button
                onClick={handleUpload}
                disabled={!pdfFile || !title.trim() || isUploading || uploadComplete}
                variant="primary"
                size="md"
                className="w-full sm:w-auto"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="animate-spin mr-2 h-4 w-4" />
                    {t('upload.buttons.uploading')}
                  </>
                ) : uploadComplete ? (
                  <>
                    <Check className="mr-2 h-4 w-4" />
                    {isQueued ? t('upload.buttons.createdProcessing') : t('upload.buttons.uploadedSuccess')}
                  </>
                ) : (
                  <>
                    <UploadIcon className="mr-2 h-4 w-4" />
                    {t('upload.buttons.upload')}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
        
        {uploadComplete && (
          <div className={`mt-6 sm:mt-8 p-4 sm:p-6 rounded-xl border ${isQueued ? 'bg-blue-50 border-blue-200' : 'bg-success-50 border-success-200'}`}>
            <div className="flex items-start gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isQueued ? 'bg-blue-100 text-blue-700' : 'bg-success-100 text-success'}`}>
                <Check size={16} />
              </div>
              <div>
                <h3 className={`font-medium ${isQueued ? 'text-blue-900' : 'text-success-800'}`}>
                  {isQueued ? t('upload.status.createdProcessingTitle') : t('upload.status.uploadedTitle')}
                </h3>
                <p className={`text-sm mt-1 ${isQueued ? 'text-blue-800' : 'text-success-700'}`}>
                  {isQueued
                    ? t('upload.status.createdProcessingText')
                    : t('upload.status.uploadedText')}
                </p>
                <div className="mt-4 flex flex-col sm:flex-row gap-3">
                  <Button
                    variant={isQueued ? 'secondary' : 'primary'}
                    size="sm"
                    onClick={() => navigate('/')}
                  >
                    {t('upload.buttons.backHome')}
                  </Button>
                  {createdBook?.id && !isQueued && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => navigate(`/documents/${createdBook.id}`)}
                    >
                      {t('upload.buttons.viewDocument')}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Upload;
